/**
 * GridSwatchQuickAdd Component
 *
 * Developer Note:
 * This component handles dynamic rendering of color swatches and size pills
 * inside product grid items, based on product data fetched from Shopify's
 * `/products/[handle].js` endpoint. It also manages swatch-based variant image updates
 * and quick-add-to-cart functionality.
 *
 * ⚠️ This script is isolated via an IIFE to prevent polluting global scope.
 * Be sure to keep CSS class and attribute names consistent with your theme setup.
 */

(() => {
  const attributes$c = {
    image: "data-swatch-image",
    handle: "data-swatch-handle",
    label: "data-swatch-label",
    scrollbar: "data-scrollbar",
    swatchCount: "data-swatch-count",
    tooltip: "data-tooltip",
    variant: "data-swatch-variant",
    variantName: "data-swatch-variant-name",
    variantTitle: "data-variant-title",
    swatchValues: "data-swatch-values"
  };

  const classes$c = {
    visible: "is-visible",
    hidden: "hidden",
    stopEvents: "no-events",
    swatch: "swatch"
  };

  /**
   * Fetch Shopify product JSON by handle
   * @param {string} handle - Shopify product handle
   * @returns {Promise<Object>} Product JSON
   */
  async function fetchProduct(handle) {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error("Failed to load product data");
    return await res.json();
  }

  class GridSwatchQuickAdd {
    /**
     * @param {HTMLElement} container - Root container for this product grid item
     */
    constructor(container) {
      this.container = container;
      this.handle = container.dataset.productHandle;
      this.productUrl = container.dataset.productUrl;
      this.productImage = this.container
        .closest("[data-grid-item]")
        ?.querySelector("[data-product-image]");
      this.colorButtons = container.querySelectorAll("[data-color-swatch]");
      this.sizeButtons = container.querySelectorAll(".variant-pill");
      this.selectedColor = null;
      this.product = null;

      this.init();
    }

    /**
     * Initialize component by fetching product data and rendering UI
     */
    async init() {
      this.product = await fetchProduct(this.handle);

      this.renderColorSwatches();
      this.colorButtons = this.container.querySelectorAll(
        "[data-color-swatch]"
      );

      this.renderSizePills();
      // this.sizeButtons = this.container.querySelectorAll('.variant-pill');

      // Always update size pills once after rendering
      this.updateSizePills();

      // Preselect the correct color swatch (either regular or sibling)
      let matchedSwatch = null;

      this.colorButtons.forEach((button) => {
        const color = button.dataset.color;
        const siblingHandle = button.dataset.siblingHandle;

        if (
          !siblingHandle &&
          this.product.variants.some(
            (v) => v.title === button.getAttribute(attributes$c.variantName)
          )
        ) {
          // Regular swatch: match by variant title
          matchedSwatch = button;
        } else if (siblingHandle && color === this.product.title) {
          // Sibling swatch: match by title
          matchedSwatch = button;
        }
      });

      if (matchedSwatch) {
        matchedSwatch.classList.add("active");

        if (matchedSwatch.dataset.siblingHandle) {
          this.showSiblingImage(matchedSwatch.dataset.color);
        } else {
          this.selectColor(matchedSwatch.dataset.color);
        }
      } else {
        this.updateSizePills();
      }

      this.bindEvents();
    }

    /**
     * Attach click events to swatches and size pills
     */
    bindEvents() {
      this.colorButtons.forEach((button) => {
        button.addEventListener("click", async () => {
          const siblingHandle = button.dataset.siblingHandle;
          const title = button.getAttribute("title");

          // Remove .active from all swatches
          this.colorButtons.forEach((btn) => btn.classList.remove("active"));

          // Add .active to clicked button
          button.classList.add("active");

          if (siblingHandle) {
            // Handle sibling swatch
            this.showSiblingImage(title);

            try {
              const siblingProduct = await fetchProduct(siblingHandle);
              this.product = siblingProduct;

              // FIX: re-render size pills for the sibling
              this.container.querySelector(".variant-pills")?.remove();
              this.renderSizePills();

              // REBIND size buttons after rendering
              this.sizeButtons = this.container.querySelectorAll(
                ".variant-pills button"
              );
              this.sizeButtons.forEach((sizeBtn) => {
                sizeBtn.addEventListener("click", () => {
                  this.handleSizeClick(sizeBtn);
                });
              });

              const color = button.dataset.color;
              this.selectColor(color);
              this.updateSizePills();
            } catch (e) {
              console.error("Failed to fetch sibling product:", e);
            }
          } else {
            // Handle regular color swatch
            const color = button.dataset.color;
            this.selectColor(color);

            // Ensure .active is correctly set
            this.colorButtons.forEach((btn) => {
              btn.classList.toggle(
                "active",
                btn.dataset.color === color && !btn.dataset.siblingHandle
              );
            });
          }
        });
      });

      this.sizeButtons.forEach((button) => {
        button.addEventListener("click", () => {
          this.handleSizeClick(button);
        });
      });
    }

    updateSoldOutBadge() {
      const card = this.container.closest(".grid-item.product-item");
      if (!card) return;
      if (card.dataset.productType == "bundle") return;
      // If there are no quick-add size pills (e.g. Gift Cards), keep Liquid badge logic untouched.
      const sizePills = this.container.querySelectorAll(".variant-pill");
      if (!sizePills.length) return;

      const imageContainer = card.querySelector(".product-item__image");

      // Find dynamic badges (created by JS)
      let soldOutBadge = card.querySelector(".badge-box[data-sold-out-badge]");
      let comingSoonBadge = card.querySelector(".badge-box[data-coming-soon-badge]");

      // Find static "Coming Soon" badge rendered by Liquid (not our dynamic ones)
      let staticComingSoonBadge = null;
      const staticBadges = imageContainer
        ? imageContainer.querySelectorAll(
            ".badge-box:not([data-sold-out-badge]):not([data-coming-soon-badge])"
          )
        : [];
      staticBadges.forEach((b) => {
        if (b.textContent.trim().toLowerCase() === "coming soon") {
          staticComingSoonBadge = b;
        }
      });

      // Check availability: any enabled size button?
      const anyEnabled = [...sizePills].some((btn) => !btn.disabled);

      // Check if current product is "Coming Soon" by supported badge tags.
      const normalizedTags = (this.product.tags || []).map((t) =>
        String(t).trim().toLowerCase()
      );
      // If Liquid already decided this customer can actually buy the item
      // (e.g. employee + employeeshop bypass on a B2B-restricted product),
      // don't synthesize a Coming Soon badge on top of it.
      // (server-rendered via `data-hide-coming-soon-badge` in product-grid-item.liquid).
      const hideComingSoonBadge = card.dataset.hideComingSoonBadge === "true";
      const isComingSoon =
        !hideComingSoonBadge &&
        (normalizedTags.includes("icon_coming soon") ||
          normalizedTags.includes("_badge_coming soon"));

      // Prevent soldout for products with the Layettesetblanketaw25 tag
      const hasLayetteTag = this.product.tags.includes("Layettesetblanketaw25");

      if (anyEnabled) {
        // There are available options. "Coming soon" products must keep their badge visible.
        if (soldOutBadge) {
          soldOutBadge.classList.add(classes$c.hidden);
          soldOutBadge.classList.remove(classes$c.stopEvents);
          soldOutBadge.setAttribute("aria-hidden", "true");
        }

        if (isComingSoon) {
          if (staticComingSoonBadge) {
            staticComingSoonBadge.classList.remove(classes$c.hidden);
          } else if (!comingSoonBadge) {
            // Fallback if Liquid did not render a static Coming Soon badge.
            comingSoonBadge = document.createElement("span");
            comingSoonBadge.className = "badge-box";
            comingSoonBadge.textContent = "Coming soon";
            comingSoonBadge.setAttribute("data-coming-soon-badge", "");

            if (imageContainer) {
              const existingBadge = imageContainer.querySelector(
                ".badge-box:not(.hidden)"
              );
              if (existingBadge) {
                comingSoonBadge.style.top = "40px";
              }
              imageContainer.appendChild(comingSoonBadge);
            } else {
              card.insertBefore(comingSoonBadge, card.firstChild);
            }
          }

          if (comingSoonBadge) {
            comingSoonBadge.classList.remove(classes$c.hidden);
            comingSoonBadge.setAttribute("aria-hidden", "false");
          }
        } else {
          if (comingSoonBadge) {
            comingSoonBadge.classList.add(classes$c.hidden);
            comingSoonBadge.setAttribute("aria-hidden", "true");
          }
          if (staticComingSoonBadge) {
            staticComingSoonBadge.classList.add(classes$c.hidden);
          }
        }
      } else if (isComingSoon) {
        // All disabled AND product is "Coming Soon" → show Coming Soon, hide Sold Out
        if (soldOutBadge) {
          soldOutBadge.classList.add(classes$c.hidden);
          soldOutBadge.classList.remove(classes$c.stopEvents);
          soldOutBadge.setAttribute("aria-hidden", "true");
        }

        // Show static Coming Soon badge if it exists from Liquid
        if (staticComingSoonBadge) {
          staticComingSoonBadge.classList.remove(classes$c.hidden);
        } else if (!comingSoonBadge) {
          // Create dynamic Coming Soon badge
          comingSoonBadge = document.createElement("span");
          comingSoonBadge.className = "badge-box";
          comingSoonBadge.textContent = "Coming soon";
          comingSoonBadge.setAttribute("data-coming-soon-badge", "");

          if (imageContainer) {
            const existingBadge = imageContainer.querySelector(
              ".badge-box:not(.hidden)"
            );
            if (existingBadge) {
              comingSoonBadge.style.top = "40px";
            }
            imageContainer.appendChild(comingSoonBadge);
          } else {
            card.insertBefore(comingSoonBadge, card.firstChild);
          }
        }

        if (comingSoonBadge) {
          comingSoonBadge.classList.remove(classes$c.hidden);
          comingSoonBadge.setAttribute("aria-hidden", "false");
        }
      } else {
        // All disabled AND NOT "Coming Soon" → show Sold Out
        // Hide Coming Soon badges
        if (comingSoonBadge) {
          comingSoonBadge.classList.add(classes$c.hidden);
          comingSoonBadge.setAttribute("aria-hidden", "true");
        }
        if (staticComingSoonBadge) {
          staticComingSoonBadge.classList.add(classes$c.hidden);
        }

        // Create Sold Out badge if missing
        if (!soldOutBadge && !hasLayetteTag) {
          soldOutBadge = document.createElement("span");
          soldOutBadge.className = "badge-box";
          soldOutBadge.textContent = "Sold Out";
          soldOutBadge.setAttribute("data-sold-out-badge", "");

          if (imageContainer) {
            const existingBadge = imageContainer.querySelector(
              ".badge-box:not(.hidden)"
            );
            if (existingBadge) {
              soldOutBadge.style.top = "40px";
            }
            imageContainer.appendChild(soldOutBadge);
          } else {
            card.insertBefore(soldOutBadge, card.firstChild);
          }
        }

        // Show sold out badge
        if (soldOutBadge) {
          soldOutBadge.classList.remove(classes$c.hidden);
          soldOutBadge.classList.add(classes$c.stopEvents);
          soldOutBadge.setAttribute("aria-hidden", "false");
        }
      }
    }

    /**
     * Show a variant image on swatch click
     * @param {MouseEvent} event
     */
    showVariantImage(event) {
      const swatchButton = event.currentTarget;
      const variantName = swatchButton
        .getAttribute(attributes$c.variantName)
        ?.replaceAll('"', "'");

      const variantImages = this.productImage.querySelectorAll(
        `[${attributes$c.variantTitle}]`
      );
      const variantImageSelected = this.productImage.querySelector(
        `[${attributes$c.variantTitle}="${variantName}"]`
      );

      variantImages?.forEach((image) =>
        image.classList.remove(classes$c.visible)
      );
      variantImageSelected?.classList.add(classes$c.visible);
    }

    /**
     * Show a variant image on swatch click
     * @param {MouseEvent} event
     */
    showSiblingImage(title) {
      if (!this.productImage) return;

      const allImages = this.productImage.querySelectorAll(
        ".product-item__bg__inner"
      );
      allImages.forEach((image) => image.classList.remove("is-visible"));

      const imageToShow = this.productImage.querySelector(
        `[data-product-title="${title.replaceAll('"', "'")}"]`
      );
      if (imageToShow) {
        imageToShow.classList.add("is-visible");
      }
    }

    /**
     * Hide all variant images (e.g., on mouseleave)
     */
    hideVariantImages() {
      this.productImage
        .querySelectorAll(`[${attributes$c.variantTitle}].${classes$c.visible}`)
        ?.forEach((image) => {
          image.classList.remove(classes$c.visible);
        });
    }

    /**
     * Select a color swatch and update image and size pills
     * @param {string} color - Selected color label
     */
    selectColor(color) {
      this.selectedColor = color;

      const matchingVariantWithImage = this.product.variants.find(
        (v) => v.options.includes(color) && v.featured_media
      );

      if (matchingVariantWithImage && this.productImage) {
        const imageUrl = `${matchingVariantWithImage.featured_media.preview_image.src}&width=400`;
        this.productImage.style.backgroundImage = `url(${imageUrl})`;
      }

      this.updateSizePills();

      const swatchBtn = [...this.colorButtons].find(
        (btn) => btn.dataset.color === color
      );
      const variantName = swatchBtn
        ?.getAttribute(attributes$c.variantName)
        ?.replaceAll('"', "'");

      if (variantName && this.productImage) {
        const variantImages = this.productImage.querySelectorAll(
          `[${attributes$c.variantTitle}]`
        );
        const selectedImage = this.productImage.querySelector(
          `[${attributes$c.variantTitle}="${variantName}"]`
        );

        variantImages.forEach((image) => {
          image.classList.remove(classes$c.visible);
        });

        selectedImage?.classList.add(classes$c.visible);
      }
    }

    /**
     * Enable or disable size pills based on current color selection
     */
    updateSizePills() {
      const optionNames = this.product.options;
      const colorIndex = optionNames.findIndex(
        (opt) => opt.name.toLowerCase() === "color"
      );
      const sizeIndex = optionNames.findIndex(
        (opt) => opt.name.toLowerCase() === "size"
      );
      const hasSiblings = !!this.container.dataset.siblings;

      // --- Parse stock JSON for variants and siblings ---
      let variantStock = [];
      let siblingStock = [];
      let isAvailable = false;

      const variantStockEl = this.container.parentElement.querySelector(
        `[data-variant-stock-${this.product.id}]`
      );
      if (variantStockEl) {
        try {
          variantStock = JSON.parse(variantStockEl.textContent);
        } catch (e) {
          console.warn("Invalid variant stock JSON", e);
        }
      }

      const siblingStockEl = this.container.parentElement.querySelector(
        `[data-sibling-stock-${this.product.id}]`
      );
      if (siblingStockEl) {
        try {
          siblingStock = JSON.parse(siblingStockEl.textContent);
        } catch (e) {
          console.warn("Invalid sibling stock JSON", e);
        }
      }

      this.sizeButtons.forEach((button) => {
        const sizeValue = button.dataset.value;
        let matchingVariant;

        if (colorIndex >= 0 && this.selectedColor !== null) {
          if (hasSiblings) {
            // Sibling mode: match based on product title
            matchingVariant = this.product.variants.find(
              (v) =>
                this.product.title === this.selectedColor &&
                v.options[sizeIndex] === sizeValue
            );
          } else {
            // Regular product: match based on color + size
            matchingVariant = this.product.variants.find(
              (v) =>
                v.options[colorIndex] === this.selectedColor &&
                v.options[sizeIndex] === sizeValue
            );
          }
        } else {
          // Only Size (no Color)
          matchingVariant = this.product.variants.find(
            (v) => v.options[sizeIndex] === sizeValue
          );
        }

        // --- HANDLE NON-EXISTENT VARIANT ---
        if (!matchingVariant) {
          button.disabled = true;
          button.dataset.variantId = "";
          return; // Skip further checks
        }

        // 1. Check in variant stock array
        let variantEntry = variantStock.find(
          (v) => v.variant_id == matchingVariant.id
        );

        let isAvailable = false; // default

        if (variantEntry) {
          isAvailable = variantEntry.quantity_available === "true";
        } else {
          // 2. Otherwise, check sibling stock array
          const siblingEntry = siblingStock.find(
            (v) => v.variant_id == matchingVariant.id
          );
          if (siblingEntry) {
            isAvailable = siblingEntry.quantity_available === "true";
          }
        }

        const hasNoShopTag =
          this.product.tags && this.product.tags.includes("noshop");

        if (
          matchingVariant.available &&
          isAvailable &&
          !hasNoShopTag
        ) {
          button.disabled = false;
          button.dataset.variantId = matchingVariant.id;
        } else {
          button.disabled = true;
          button.dataset.variantId = "";
        }
      });


      this.updateSoldOutBadge();
    }

    /**
     * Render color swatches dynamically from product options and siblings
     */
    renderColorSwatches() {
      const values = this.container.dataset.swatchValues;
      const siblingsJSON = this.container.dataset.siblings;

      const swatchContainer = document.createElement("div");
      swatchContainer.classList.add("color-variant-pills");

      // --- Render color swatches for current product ---
      if (values && this.product) {
        const colorValues = values.split(",").map((pair) => {
          const [label, handle] = pair.split(":");
          return { label, handle };
        });

        colorValues.forEach(({ label, handle }) => {
          let variant = null;
          let image = "";

          for (const productVariant of this.product.variants) {
            const matchesColor = productVariant.options.includes(label);
            if (matchesColor) {
              variant = productVariant;
              if (productVariant.featured_media) {
                image = productVariant.featured_media.preview_image.src;
                break;
              }
            }
          }

          const btn = document.createElement("button");
          btn.className = "color-variant-pill";
          btn.type = "button";
          btn.setAttribute("data-color-swatch", "");
          btn.setAttribute("data-color", label);
          btn.setAttribute("title", label);

          if (variant) {
            btn.setAttribute(
              attributes$c.variantName,
              variant.title.replaceAll('"', "'")
            );
            btn.setAttribute(attributes$c.variant, variant.id);
            btn.setAttribute(attributes$c.image, image);
          }

          if (image) {
            btn.style.setProperty("background-image", `url(${image}&width=60)`);
            btn.style.setProperty("background-size", "cover");
            btn.style.setProperty("background-position", "center");
          } else {
            btn.style.setProperty("--swatch", `var(--${handle})`);
            btn.style.background = `var(--${handle}, #f2f2f2)`;
          }

          swatchContainer.appendChild(btn);
        });
      }

      // --- Render sibling product swatches (as color options) ---
      if (siblingsJSON) {
        let siblings;
        try {
          siblings = JSON.parse(siblingsJSON);
        } catch (e) {
          console.warn("Invalid siblings JSON", e);
          siblings = [];
        }

        siblings.forEach((siblingProduct) => {
          const title = siblingProduct.title;
          const handle = siblingProduct.handle;
          const image = siblingProduct.featured_image;

          const btn = document.createElement("button");
          btn.className = "color-variant-pill";
          btn.type = "button";
          btn.setAttribute("data-color-swatch", "");
          btn.setAttribute("data-color", title);
          btn.setAttribute("data-sibling-handle", handle);
          btn.setAttribute("title", title);

          if (image) {
            btn.style.setProperty("background-image", `url(${image}&width=60)`);
            btn.style.setProperty("background-size", "200%");
            btn.style.setProperty("background-position", "center");
          } else {
            btn.style.background = "#f2f2f2";
          }

          swatchContainer.appendChild(btn);
        });
      }

      this.container.appendChild(swatchContainer);
    }

    /**
     * Render size pill buttons dynamically from product options
     */
    renderSizePills() {
      if (!this.product) return;

      const sizeOption = this.product.options.find(
        (opt) => opt.name.toLowerCase() === "size"
      );
      if (!sizeOption) return;

      const pillsWrapper = document.createElement("div");
      pillsWrapper.className = "variant-pills";
      pillsWrapper.dataset.optionName = "size";
      pillsWrapper.dataset.productUrl = this.productUrl;

      sizeOption.values.forEach((size) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "variant-pill";
        button.dataset.value = size;

        button.innerHTML = `
          <span class="option-title">${size}</span>
          <div class="spinner spinner--dark hidden"></div>
          <span class="variant-id hidden" data-variant-id=""></span>
        `;

        pillsWrapper.appendChild(button);
      });

      this.container.appendChild(pillsWrapper);
      this.sizeButtons = this.container.querySelectorAll(".variant-pill");
    }

    /**
     * Handle click on a size pill to add product to cart
     * @param {HTMLElement} button - Clicked size pill
     */
    handleSizeClick(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) {
        console.warn("No valid variant for this combination.");
        return;
      }

      this.sizeButtons.forEach((pill) => {
        pill.classList.remove("active");
        pill.querySelector(".spinner")?.classList.add("hidden");
        pill.querySelector(".option-title")?.classList.remove("hidden");
      });

      button.classList.add("active");
      const spinner = button.querySelector(".spinner");
      const title = button.querySelector(".option-title");
      spinner?.classList.remove("hidden");
      title?.classList.add("hidden");

      fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      })
        .then((res) => res.json())
        .then((data) => {
          spinner?.classList.add("hidden");
          title?.classList.remove("hidden");

          const event = new CustomEvent("theme:cart:refresh", {
            detail: { variantId, quantity: 1, cartItem: data }
          });
          document.dispatchEvent(event);
        })
        .catch((err) => {
          console.error("Cart add failed:", err);
          spinner?.classList.add("hidden");
          title?.classList.remove("hidden");
        });
    }
  }

  /**
   * Auto-init GridSwatchQuickAdd for all applicable containers
   */
  document.addEventListener("DOMContentLoaded", () => {
    // Initial load
    initGridSwatchQuickAdd();

    // Observe product grid changes
    const productsGrid = document.querySelector("[data-products-grid]");
    if (productsGrid) {
      const observer = new MutationObserver((mutationsList, observer) => {
        // When inner HTML changes, reinitialize components
        for (const mutation of mutationsList) {
          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            initGridSwatchQuickAdd();
            break;
          }
        }
      });

      observer.observe(productsGrid, {
        childList: true,
        subtree: true,
      });
    }
  });

  function initGridSwatchQuickAdd() {
    document
      .querySelectorAll('[data-component="GridSwatchQuickAdd"]')
      .forEach((el) => {
        // Skip grid-item/product-item cards living inside a
        // data-section-type="products-image" section — this script
        // must not touch those.
        if (el.closest('[data-section-type="products-image"]')) return;

        // Avoid double initialization
        if (!el.dataset.swatchInit) {
          el.dataset.swatchInit = "true";
          new GridSwatchQuickAdd(el);
        }
      });
  }

})();